import React from 'react';

const externalLinkIcon = (
  <svg width="11" height="11" viewBox="0 0 10 10" fill="none" aria-hidden="true">
    <path
      d="M2 8L8 2M8 2H4M8 2v4"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export function ReferencePill({ reference: refItem, onClick, variant = 'default' }) {
  const label = (refItem?.label || refItem?.value || '').trim();
  if (!label) return null;

  const clickable = !!(refItem?.originalPdfKey && onClick);
  const type = refItem?.type || '';
  const showTypePrefix = variant !== 'table' && type;

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={`reference-pill${clickable ? ' clickable' : ''}${variant === 'table' ? ' reference-pill--table' : ''}`}
      title={label}
      onClick={clickable ? (e) => { e.stopPropagation(); onClick(refItem); } : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onClick(refItem);
              }
            }
          : undefined
      }
    >
      {showTypePrefix ? <strong>{type}: </strong> : null}
      {label}
    </div>
  );
}

export function DocEvidencePill({ title, pageNumber, clickable, onClick }) {
  const pageSuffix = pageNumber ? ` · p. ${pageNumber}` : '';
  const displayTitle = (title && title !== '—' ? title : '').trim();
  const label = displayTitle ? `${displayTitle}${pageSuffix}` : '';

  if (!label) {
    return <span className="td-doc-empty">—</span>;
  }

  if (!clickable) {
    return (
      <span
        className="reference-pill reference-pill--table evidence-doc-pill evidence-doc-pill--static"
        title={label}
      >
        <span className="evidence-doc-pill__label">{label}</span>
      </span>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      className="reference-pill reference-pill--table clickable evidence-doc-pill"
      title={label}
      aria-label={`Open evidence: ${label}`}
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
    >
      {externalLinkIcon}
      <span className="evidence-doc-pill__label">{label}</span>
    </div>
  );
}

/** Sidebar: text-style reference links (not table pills). */
export function PanelReferenceList({ refs, onRefClick }) {
  if (!refs?.length) {
    return <span className="panel-empty-hint">No references</span>;
  }
  return (
    <ul className="panel-ref-list">
      {refs.map((r, idx) => {
        const label = r.label || r.value || '—';
        const clickable = !!(r.originalPdfKey && onRefClick);
        const prefix = r.type ? `${r.type} · ` : '';
        return (
          <li key={`${r.type}-${r.value}-${idx}`}>
            {clickable ? (
              <button
                type="button"
                className="panel-ref-list__link"
                title={label}
                onClick={(e) => {
                  e.stopPropagation();
                  onRefClick(r);
                }}
              >
                <span className="panel-ref-list__prefix">{prefix}</span>
                {label}
              </button>
            ) : (
              <span className="panel-ref-list__static">
                <span className="panel-ref-list__prefix">{prefix}</span>
                {label}
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export function ReferencePillsRow({ refs, onRefClick }) {
  if (!refs?.length) {
    return <span className="td-ref-pill">—</span>;
  }
  return (
    <div className="reference-pills-row reference-pills-row--compact" onClick={(e) => e.stopPropagation()}>
      {refs.map((r, idx) => (
        <ReferencePill
          key={`${r.type}-${r.value}-${idx}`}
          reference={r}
          onClick={r.originalPdfKey ? onRefClick : undefined}
        />
      ))}
    </div>
  );
}
